import type { PriceObservation, ShoppingListItem, Store } from '@recipes/contracts';
import {
  compareStores,
  currentPrice,
  dayString,
  estimateList,
  priceChange,
  type CurrentPrice,
  type EstimateItem,
  type EstimateProduct,
  type ListEstimate,
  type PriceChange,
  type StoreComparison,
  type StoreRef,
} from '@recipes/domain';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useMemo } from 'react';
import { useConfig, useFeature, useProfile } from '@/features/shared/hooks';
import { useShoppingHistory, useShoppingPreferences } from '@/features/shopping/hooks';
import { currentFormatLocale } from '@/lib/i18n';
import { offlineDb } from '@/lib/offline/db';

/**
 * Prices on the device (docs/partial/shopping-in-store-spec.md 6.3–6.8). Everything here reads
 * the device copy and computes with the pure rules of `@recipes/domain` – the same ones the API
 * uses – so prices, the estimate and price changes work at the shelf without a connection.
 */

const CURRENCY_KEY = 'prices:currency';

/** The reader's currency: the profile's choice, else the instance default; remembered for offline. */
export function useCurrency(): string {
  const { data: profile } = useProfile();
  const { data: config } = useConfig();
  const fromProfile = profile?.effectiveCurrency;
  useEffect(() => {
    if (!fromProfile) return;
    try {
      localStorage.setItem(CURRENCY_KEY, fromProfile);
    } catch {
      // private mode: known for this page only
    }
  }, [fromProfile]);
  if (fromProfile) return fromProfile;
  try {
    const stored = localStorage.getItem(CURRENCY_KEY);
    if (stored) return stored;
  } catch {
    // fall through
  }
  return config?.prices?.defaultCurrency ?? 'EUR';
}

export interface PriceRules {
  staleDays: number;
  minPercent: number;
  minProducts: number;
}

export function usePriceRules(): PriceRules {
  const { data: config } = useConfig();
  return useMemo(
    () => ({
      staleDays: config?.prices?.staleDays ?? 90,
      minPercent: config?.prices?.changeMinPercent ?? 3,
      minProducts: config?.prices?.indexMinProducts ?? 5,
    }),
    [config],
  );
}

/** Prices are shown when the instance offers them and the user did not switch them off. */
export function usePricesEnabled(): boolean {
  const feature = useFeature('prices');
  const catalog = useFeature('ingredient_catalog');
  const preferences = useShoppingPreferences();
  return feature && catalog && preferences.showPrices;
}

export const today = (): string => dayString(new Date());

export const storeRefOf = (store: Pick<Store, 'id' | 'parentStoreId'>): StoreRef => ({
  id: store.id,
  chainId: store.parentStoreId ?? store.id,
});

export function formatMoney(amount: string, currency: string): string {
  try {
    return new Intl.NumberFormat(currentFormatLocale(), { style: 'currency', currency }).format(
      // Display only: the amount itself stays a decimal string everywhere else.
      Number(amount),
    );
  } catch {
    return `${amount} ${currency}`;
  }
}

/** Signed difference with the currency, e.g. "+0,20 €". */
export function formatMoneyDelta(amount: string, currency: string): string {
  const negative = amount.startsWith('-');
  const text = formatMoney(negative ? amount.slice(1) : amount, currency);
  return `${negative ? '−' : '+'}${text}`;
}

export function formatDay(day: string): string {
  return new Intl.DateTimeFormat(currentFormatLocale(), { dateStyle: 'short' }).format(
    new Date(`${day}T12:00:00`),
  );
}

/** All prices of the household on this device. */
export function useAllPrices(): PriceObservation[] {
  const rows = useLiveQuery(() => offlineDb.catalogPrices.toArray(), []);
  return useMemo(() => (rows ?? []).map((row) => row.price), [rows]);
}

export function usePricesFor(target: {
  productId?: string | null;
  ingredientId?: string | null;
}): PriceObservation[] {
  const { productId, ingredientId } = target;
  const rows = useLiveQuery(
    () =>
      productId
        ? offlineDb.catalogPrices.where('productId').equals(productId).toArray()
        : ingredientId
          ? offlineDb.catalogPrices.where('ingredientId').equals(ingredientId).toArray()
          : [],
    [productId, ingredientId],
  );
  return useMemo(() => (rows ?? []).map((row) => row.price), [rows]);
}

export interface PriceHere {
  current: CurrentPrice<PriceObservation>;
  /** Against the previous regular price at the same chain; null when there is none. */
  change: PriceChange<PriceObservation> | null;
}

/** "Price here": branch → chain → none, in the reader's currency, with its change. */
export function priceHere(
  observations: readonly PriceObservation[],
  store: StoreRef,
  currency: string,
  rules: PriceRules,
): PriceHere | null {
  const current = currentPrice(observations, {
    store,
    currency,
    today: today(),
    staleDays: rules.staleDays,
  });
  if (!current) return null;
  const place = current.observation.chainId ?? current.observation.storeId;
  const change = priceChange(
    observations.filter((o) => o.currency === currency && (o.chainId ?? o.storeId) === place),
    { at: today(), minPercent: rules.minPercent },
  );
  return { current, change };
}

const toEstimateItem = (item: ShoppingListItem): EstimateItem => ({
  id: item.id,
  name: item.name,
  checked: item.checked,
  productId: item.product?.id ?? null,
  ingredientId: item.ingredientId ?? null,
  quantity: item.quantity,
  unitCode: item.unitCode,
});

function useEstimateProducts(): EstimateProduct[] {
  const rows = useLiveQuery(() => offlineDb.catalogProducts.toArray(), []);
  return useMemo(
    () =>
      (rows ?? []).map(({ product }) => ({
        id: product.id,
        name: product.brand ? `${product.brand} ${product.name}` : product.name,
        ingredientId: product.ingredient?.id ?? null,
        packageQuantity: product.packageQuantity,
        packageUnitCode: product.packageUnitCode,
      })),
    [rows],
  );
}

/** Learned pairings: the product this household scanned for a name before. */
export function useLearnedPairings(): Record<string, string> {
  const history = useShoppingHistory();
  return useMemo(() => {
    const learned: Record<string, string> = {};
    for (const entry of history)
      if (entry.lastProductId) learned[entry.normalizedName] = entry.lastProductId;
    return learned;
  }, [history]);
}

export interface ItemPriceInfo {
  /** What the item adds to the estimate at the chosen store. */
  amount: string;
  stale: boolean;
  /** Noticeable change of the product's price at this chain; null otherwise. */
  change: PriceChange<PriceObservation> | null;
}

/** Price and change per open item at the chosen store – for the rows of the list (spec 5.5, 5.7). */
export function useItemPrices(
  here: ListEstimate<PriceObservation> | null,
): Map<string, ItemPriceInfo> {
  const observations = useAllPrices();
  const rules = usePriceRules();
  return useMemo(() => {
    const out = new Map<string, ItemPriceInfo>();
    if (!here) return out;
    for (const line of here.lines) {
      const place = line.observation.chainId ?? line.observation.storeId;
      const change = line.productId
        ? priceChange(
            observations.filter(
              (o) =>
                o.productId === line.productId &&
                o.currency === here.currency &&
                (o.chainId ?? o.storeId) === place,
            ),
            { at: today(), minPercent: rules.minPercent },
          )
        : null;
      out.set(line.itemId, {
        amount: line.amount,
        stale: line.stale,
        change: change?.noticeable ? change : null,
      });
    }
    return out;
  }, [here, observations, rules.minPercent]);
}

export interface ListEstimates {
  /** The store chosen in "Shopping at …"; null without a choice. */
  here: ListEstimate<PriceObservation> | null;
  /** Every chain the household has prices for (the chosen store stands for its chain). */
  comparison: StoreComparison<PriceObservation>;
  stores: Array<StoreRef & { name: string }>;
  products: Map<string, string>;
  currency: string;
}

/**
 * The estimate for the chosen store and for every other store with prices, recomputed whenever
 * an item, a price or the chosen store changes – also without a connection.
 */
export function useListEstimates(
  items: readonly ShoppingListItem[],
  store: Store | null,
  allStores: readonly Store[],
): ListEstimates {
  const observations = useAllPrices();
  const products = useEstimateProducts();
  const learned = useLearnedPairings();
  const currency = useCurrency();
  const rules = usePriceRules();
  return useMemo(() => {
    const options = { currency, today: today(), staleDays: rules.staleDays, learned };
    const estimateItems = items.map(toEstimateItem);
    const chosen = store ? { ...storeRefOf(store), name: store.name } : null;
    const chainIds = new Set<string>();
    for (const observation of observations) {
      if (observation.currency !== currency) continue;
      const chain = observation.chainId ?? observation.storeId;
      if (chain) chainIds.add(chain);
    }
    if (chosen) chainIds.delete(chosen.chainId ?? chosen.id);
    const chains = allStores
      .filter((candidate) => chainIds.has(candidate.id))
      .map((candidate) => ({ id: candidate.id, chainId: candidate.id, name: candidate.name }));
    const stores = [...(chosen ? [chosen] : []), ...chains];
    return {
      here: chosen
        ? estimateList(estimateItems, observations, products, { ...options, store: chosen })
        : null,
      comparison: compareStores(estimateItems, observations, products, stores, options),
      stores,
      products: new Map(products.map((product) => [product.id, product.name])),
      currency,
    };
  }, [allStores, currency, items, learned, observations, products, rules.staleDays, store]);
}
